import { useState, useMemo } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import { format, parseISO, isValid } from "date-fns";
import { LoaderCircle, Pencil } from "lucide-react";
import AddMaintenanceScheduleSidebar from "./AddMaintenanceScheduleSidebar";

export default function MaintenanceSchedulesCalendar({
    devices = [],
    onRefresh = async () => {},
    filterText = "",
}) {
    const [isAdding, setIsAdding] = useState(false);
    const [modalItem, setModalItem] = useState(null);
    const [currentView, setCurrentView] = useState("dayGridMonth");
    const [scheduleToEdit, setScheduleToEdit] = useState(null);

    const handleAfterCreate = async () => {
        await onRefresh();
        setIsAdding(false);
        setScheduleToEdit(null);
    };

    const getColorForDates = (start, end) => {
        const now = new Date();
        if (now < start) return "#3b82f6";
        if (end && now > end) return "#6b7280";
        return "#10b981";
    };

    const coerceISO = (v) => {
        if (!v) return null;
        if (typeof v === "string") {
            const d = parseISO(v);
            return isValid(d) ? d : null;
        }
        if (v instanceof Date && isValid(v)) return v;
        try {
            const d = parseISO(String(v));
            return isValid(d) ? d : null;
        } catch {
            return null;
        }
    };

    const parseScheduleWindow = (schedule) => {
        let attrs = schedule?.schedule_attributes;
        if (!attrs) return { start: null, end: null };

        if (typeof attrs === "string") {
            try {
                attrs = JSON.parse(attrs);
            } catch {
                attrs = { when: attrs };
            }
        }

        const start = coerceISO(attrs?.from);
        const end = coerceISO(attrs?.to);

        return { start, end };
    };

    const calendarEvents = useMemo(() => {
        const events = [];
        const q = filterText?.trim().toLowerCase();
        const filteredDevices = !q
            ? devices
            : devices.filter((dev) =>
                  String(dev.name || dev.id || "")
                      .toLowerCase()
                      .includes(q)
              );

        filteredDevices.forEach((dev) => {
            (dev.maintenance_schedules || []).forEach((ms, idx) => {
                const { start, end } = parseScheduleWindow(ms);
                if (!start) return;

                const color = getColorForDates(start, end || start);
                const title = `${dev.name || dev.id || "Device"}`;

                if (currentView === "dayGridMonth") {
                    events.push({
                        id: `${dev.id}-${idx}`,
                        title,
                        start: start.toISOString(),
                        end: end ? end.toISOString() : undefined,
                        backgroundColor: color + "1A",
                        borderColor: color,
                        textColor: "#000000",
                        extendedProps: {
                            device: dev,
                            schedule: ms,
                            start,
                            end,
                        },
                    });
                } else {
                    const startDay = new Date(
                        start.getFullYear(),
                        start.getMonth(),
                        start.getDate()
                    );
                    const endRef = end || start;
                    const endDay = new Date(
                        endRef.getFullYear(),
                        endRef.getMonth(),
                        endRef.getDate()
                    );
                    const totalDays = Math.max(
                        0,
                        Math.floor((endDay - startDay) / (1000 * 60 * 60 * 24))
                    );

                    events.push({
                        id: `${dev.id}-${idx}-start`,
                        title,
                        start: start.toISOString(),
                        end:
                            totalDays > 0
                                ? new Date(
                                      startDay.getFullYear(),
                                      startDay.getMonth(),
                                      startDay.getDate() + 1
                                  ).toISOString()
                                : end
                                ? end.toISOString()
                                : undefined,
                        backgroundColor: color + "1A",
                        borderColor: color,
                        textColor: "#000000",
                        extendedProps: {
                            device: dev,
                            schedule: ms,
                            start,
                            end,
                        },
                    });

                    for (let i = 1; i < totalDays; i++) {
                        const midDay = new Date(startDay);
                        midDay.setDate(midDay.getDate() + i);
                        events.push({
                            id: `${dev.id}-${idx}-mid-${i}`,
                            title: `[Continues] ${title}`,
                            start: midDay.toISOString(),
                            allDay: true,
                            backgroundColor: color + "1A",
                            borderColor: color,
                            textColor: "#000000",
                            extendedProps: {
                                device: dev,
                                schedule: ms,
                                start,
                                end,
                            },
                        });
                    }

                    if (totalDays > 0) {
                        const endStart = new Date(endDay);
                        events.push({
                            id: `${dev.id}-${idx}-end`,
                            title,
                            start: endStart.toISOString(),
                            end: endRef.toISOString(),
                            backgroundColor: color + "1A",
                            borderColor: color,
                            textColor: "#000000",
                            extendedProps: {
                                device: dev,
                                schedule: ms,
                                start,
                                end,
                            },
                        });
                    }
                }
            });
        });
        return events;
    }, [devices, currentView, filterText]);

    const handleEventClick = (eventInfo) => {
        const { device, schedule, start, end } = eventInfo.event.extendedProps;
        const attrs =
            typeof schedule?.schedule_attributes === "string"
                ? (() => {
                      try {
                          return JSON.parse(schedule.schedule_attributes);
                      } catch {
                          return { when: schedule.schedule_attributes };
                      }
                  })()
                : schedule?.schedule_attributes || {};
        setModalItem({
            device,
            schedule,
            start,
            end,
            attrs,
        });
    };

    return (
        <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-3 tw-gap-6 tw-relative">
            <div className="lg:tw-col-span-3">
                <div className="tw-rounded-lg tw-border tw-bg-white tw-shadow-sm">
                    <div className="tw-p-6">
                        <FullCalendar
                            plugins={[
                                dayGridPlugin,
                                timeGridPlugin,
                                interactionPlugin,
                            ]}
                            initialView="dayGridMonth"
                            viewDidMount={(arg) =>
                                setCurrentView(arg.view.type)
                            }
                            headerToolbar={{
                                left: "prev,next today",
                                center: "title",
                                right: "dayGridMonth,timeGridWeek,timeGridDay",
                            }}
                            events={calendarEvents}
                            eventClick={handleEventClick}
                            height="auto"
                            eventDisplay="block"
                            dayMaxEvents={3}
                            moreLinkClick="day"
                            selectable={true}
                            selectMirror={true}
                            eventAllDayDefault={false}
                            dayHeaderFormat={{ weekday: "short" }}
                            eventTimeFormat={{
                                hour: "numeric",
                                minute: "2-digit",
                                meridiem: "short",
                            }}
                        />
                    </div>
                </div>
            </div>

            {modalItem && (
                <div className="tw-fixed tw-inset-0 tw-bg-black/50 tw-flex tw-items-center tw-justify-center tw-z-50">
                    <div className="tw-bg-white tw-rounded-xl tw-shadow-xl tw-max-w-lg tw-w-full tw-p-6 tw-relative">
                        <button
                            className="tw-absolute tw-top-2 tw-right-2 tw-text-gray-500 hover:tw-text-black"
                            onClick={() => setModalItem(null)}
                        >
                            &times;
                        </button>
                        <h3 className="tw-text-lg tw-font-bold tw-mb-2 tw-flex tw-justify-between tw-items-center">
                            <span>
                                {modalItem.device?.name || modalItem.device?.id}
                            </span>
                            <button
                                className="tw-text-blue-600 hover:tw-text-blue-800 tw-ml-2"
                                onClick={() => {
                                    setScheduleToEdit({
                                        ...modalItem.schedule,
                                        device_id: modalItem.device?.id,
                                    });
                                    setIsAdding(true);
                                    setModalItem(null);
                                }}
                                title="Edit Maintenance"
                            >
                                {isAdding &&
                                scheduleToEdit?.id === modalItem.schedule.id ? (
                                    <LoaderCircle className="tw-h-4 tw-w-4 tw-animate-spin" />
                                ) : (
                                    <Pencil className="tw-h-4 tw-w-4" />
                                )}
                            </button>
                        </h3>

                        <hr className="tw-border tw-border-solid tw-border-black" />

                        <div className="tw-text-sm tw-space-y-1">
                            <div>
                                <span className="tw-font-medium">Device:</span>
                                {modalItem.device?.name || modalItem.device?.id}
                            </div>
                            {modalItem.start && (
                                <div>
                                    <span className="tw-font-medium">
                                        When/From:
                                    </span>{" "}
                                    {format(modalItem.start, "PPpp")}
                                </div>
                            )}
                            {modalItem.end && (
                                <div>
                                    <span className="tw-font-medium">To:</span>{" "}
                                    {format(modalItem.end, "PPpp")}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            <AddMaintenanceScheduleSidebar
                isOpen={isAdding}
                onClose={() => setIsAdding(false)}
                onCreated={handleAfterCreate}
                devices={devices}
                type={scheduleToEdit ? "edit" : "add"}
                schedule={scheduleToEdit}
            />
        </div>
    );
}
